import React, { useEffect, useMemo, useState } from 'react';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../../services/data';
import './Dashboard.css';

const levelLabels = {
  critical: 'Critical',
  warning: 'Warning',
  moderate: 'Moderate',
};

const monitoringIntervalOptions = [
  { value: '3', label: 'Ultimele 3 luni' },
  { value: '6', label: 'Ultimele 6 luni' },
  { value: '12', label: 'Ultimele 12 luni' },
  { value: '24', label: 'Ultimele 24 luni' },
  { value: 'all', label: 'Toată perioada' },
  { value: 'custom', label: 'Interval personalizat' },
];

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [detailResult, setDetailResult] = useState(null);
  const [expandedKeywords, setExpandedKeywords] = useState({});
  const [expandedLevels, setExpandedLevels] = useState({});
  const [trademarkSearch, setTrademarkSearch] = useState('');
  const [selectedTrademarkMonth, setSelectedTrademarkMonth] = useState('');
  const [monitoringInterval, setMonitoringInterval] = useState('all');
  const [customStartLabel, setCustomStartLabel] = useState('');
  const [customEndLabel, setCustomEndLabel] = useState('');

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await dashboardService.getStats();
        setStats(data);
      } catch (requestError) {
        setError(requestError.message || 'Nu am putut încărca dashboard-ul.');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const trademarkMonthOptions = useMemo(
    () => stats?.monitoring_results_months || [],
    [stats]
  );

  const monitoringByTrademarkMonth = useMemo(
    () => stats?.monitoring_results_by_trademark_month || {},
    [stats]
  );

  useEffect(() => {
    if (!trademarkMonthOptions.length) {
      setSelectedTrademarkMonth('');
      return;
    }

    if (!selectedTrademarkMonth || !trademarkMonthOptions.some((item) => item.key === selectedTrademarkMonth)) {
      setSelectedTrademarkMonth(trademarkMonthOptions[0].key);
    }
  }, [selectedTrademarkMonth, trademarkMonthOptions]);

  const monitoringByTrademarkList = useMemo(() => {
    const baseList = selectedTrademarkMonth && monitoringByTrademarkMonth[selectedTrademarkMonth]
      ? monitoringByTrademarkMonth[selectedTrademarkMonth]
      : (stats?.monitoring_results_by_trademark || []);

    const term = trademarkSearch.trim().toLowerCase();

    return [...baseList]
      .sort((left, right) => String(left.label || '').localeCompare(String(right.label || ''), undefined, { sensitivity: 'base' }))
      .filter((item) => !term || String(item.label || '').toLowerCase().includes(term));
  }, [monitoringByTrademarkMonth, selectedTrademarkMonth, stats, trademarkSearch]);

  const activeMonitoringCount = stats?.monitoring_status?.active || 0;
  const inactiveMonitoringCount = stats?.monitoring_status?.inactive || 0;
  const totalMonitoringCount = activeMonitoringCount + inactiveMonitoringCount;
  const activeMonitoringPercent = totalMonitoringCount
    ? Math.round((activeMonitoringCount / totalMonitoringCount) * 100)
    : 0;

  const monitoringStatusData = useMemo(() => {
    const total = Math.max(totalMonitoringCount, 1);
    const ringRotation = -220;
    const ringCircumference = 320;

    return {
      datasets: [
        {
          label: 'Active',
          data: [activeMonitoringCount, total - activeMonitoringCount],
          backgroundColor: ['#697cff', '#e8ebf3'],
          borderWidth: 0,
          borderRadius: 8,
          borderSkipped: false,
          radius: '98%',
          cutout: '78%',
          rotation: ringRotation,
          circumference: ringCircumference,
        },
        {
          label: 'Inactive',
          data: [inactiveMonitoringCount, total - inactiveMonitoringCount],
          backgroundColor: ['#ff7a45', '#e8ebf3'],
          borderWidth: 0,
          borderRadius: 8,
          borderSkipped: false,
          radius: '80%',
          cutout: '73%',
          rotation: ringRotation,
          circumference: ringCircumference,
        },
      ],
    };
  }, [activeMonitoringCount, inactiveMonitoringCount, totalMonitoringCount]);

  const monitoringTimeline = useMemo(
    () => stats?.monitoring_results_by_month || [],
    [stats]
  );

  useEffect(() => {
    if (!monitoringTimeline.length) {
      setCustomStartLabel('');
      setCustomEndLabel('');
      return;
    }

    const availableLabels = new Set(monitoringTimeline.map((item) => item.label));
    const firstLabel = monitoringTimeline[0].label;
    const lastLabel = monitoringTimeline[monitoringTimeline.length - 1].label;

    setCustomStartLabel((prev) => (prev && availableLabels.has(prev) ? prev : firstLabel));
    setCustomEndLabel((prev) => (prev && availableLabels.has(prev) ? prev : lastLabel));
  }, [monitoringTimeline]);

  const filteredMonitoringTimeline = useMemo(() => {
    if (!monitoringTimeline.length) {
      return [];
    }

    if (monitoringInterval === 'custom') {
      const startIndex = monitoringTimeline.findIndex((item) => item.label === customStartLabel);
      const endIndex = monitoringTimeline.findIndex((item) => item.label === customEndLabel);

      if (startIndex === -1 || endIndex === -1) {
        return monitoringTimeline;
      }

      const from = Math.min(startIndex, endIndex);
      const to = Math.max(startIndex, endIndex);
      return monitoringTimeline.slice(from, to + 1);
    }

    if (monitoringInterval === 'all') {
      return monitoringTimeline;
    }

    const monthCount = Number.parseInt(monitoringInterval, 10);
    if (Number.isNaN(monthCount) || monthCount <= 0) {
      return monitoringTimeline;
    }

    return monitoringTimeline.slice(-monthCount);
  }, [customEndLabel, customStartLabel, monitoringInterval, monitoringTimeline]);

  const monitoringResultsData = useMemo(() => {
    const labels = filteredMonitoringTimeline.map((item) => item.label);
    const values = filteredMonitoringTimeline.map((item) => item.total_matches);

    return {
      labels,
      datasets: [
        {
          label: 'Total rezultate monitorizare',
          data: values,
          borderRadius: 8,
          borderSkipped: false,
          backgroundColor: '#000',
        },
      ],
    };
  }, [filteredMonitoringTimeline]);

  if (loading) {
    return <div className="dashboard-loading">Se încarcă datele dashboard-ului...</div>;
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }
    if (value.length >= 10) {
      return value.slice(0, 10);
    }
    return value;
  };

  const getKeywordError = (keywordData) => (keywordData?.error || keywordData?.partial_error ? 'TMView error' : '');

  const getKeywordResultsLabel = (keywordData) => {
    if (getKeywordError(keywordData)) {
      return 'TMView error';
    }
    if (typeof keywordData?.total_results === 'number') {
      return `${keywordData.total_results} results`;
    }
    return '0 results';
  };

  const closeDetailModal = () => {
    setIsDetailOpen(false);
    setDetailLoading(false);
    setDetailError('');
    setDetailResult(null);
    setExpandedKeywords({});
    setExpandedLevels({});
  };

  const deleteMonitoringResult = async () => {
    if (!detailResult?.id) {
      return;
    }

    if (!window.confirm('Sigur vrei să ştergi acest rezultat de monitorizare pentru această lună?')) {
      return;
    }

    setDeleting(true);
    setDetailError('');

    try {
      await dashboardService.deleteMonitoringResult(detailResult.id);
      const updatedStats = await dashboardService.getStats();
      setStats(updatedStats);
      closeDetailModal();
    } catch (requestError) {
      setDetailError(
        requestError.response?.data?.detail || requestError.message || 'Nu am putut şterge rezultatul.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const openDetailModal = async (resultId) => {
    if (!resultId) {
      return;
    }

    setIsDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailResult(null);
    setExpandedKeywords({});
    setExpandedLevels({});

    try {
      const data = await dashboardService.getMonitoringResultDetail(resultId);
      setDetailResult(data);
    } catch (requestError) {
      setDetailError(requestError.response?.data?.detail || requestError.message || 'Nu am putut încărca detaliile.');
    } finally {
      setDetailLoading(false);
    }
  };

  const summary = stats?.summary || {};

  return (
    <div className="dashboard">
      <div className="dashboard-summary-grid">
        <div className="summary-card">
          <p className="summary-label">Clienți</p>
          <h3>{summary.total_clienti || 0}</h3>
        </div>
        <div className="summary-card">
          <p className="summary-label">Monitorizări</p>
          <h3>{summary.total_monitorizari || 0}</h3>
        </div>
        <div className="summary-card">
          <p className="summary-label">Monitorizări Active</p>
          <h3>{summary.active_monitorizari || 0}</h3>
        </div>
      </div>

      <div className="dashboard-chart-grid">
        <div className="chart-card chart-wide">
          <div className="chart-card-header">
            <h4>Rezultate monitorizare per marcă</h4>
            <div className="dashboard-results-controls">
              <input
                type="text"
                className="dashboard-results-search"
                placeholder="Caută marcă..."
                value={trademarkSearch}
                onChange={(event) => setTrademarkSearch(event.target.value)}
              />
              <select
                className="dashboard-interval-select dashboard-results-month-select"
                value={selectedTrademarkMonth}
                onChange={(event) => setSelectedTrademarkMonth(event.target.value)}
                disabled={!trademarkMonthOptions.length}
              >
                {trademarkMonthOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="dashboard-results-panel">
            <div className="dashboard-results-header">
              <span>Marcă</span>
              <span>Rezultate</span>
            </div>

            <div className="dashboard-results-list">
              {monitoringByTrademarkList.map((item, index) => (
                <div className="dashboard-results-row" key={`${item.label}-${item.latest_result_id || index}`}>
                  
                  <button
                    type="button"
                    className="dashboard-results-value dashboard-results-value-link"
                    onClick={() => openDetailModal(item.latest_result_id)}
                  >
                  <span className="dashboard-results-name">{item.label.slice(0,30)}</span>
                    {item.total_matches}
                  </button>
                </div>
              ))}

              {!monitoringByTrademarkList.length && (
                <div className="dashboard-results-empty">Nu există rezultate de afișat.</div>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-right-stack">
          <div className="chart-card chart-small dashboard-status-card">
            <h4>Status monitorizare</h4>
            <div className="dashboard-status-card-body">
              <div className="dashboard-status-donut-wrap">
                <Doughnut
                  data={monitoringStatusData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: { enabled: false },
                    },
                    events: [],
                  }}
                />

                <div className="dashboard-status-center">
                  <span className="dashboard-status-percent">{activeMonitoringPercent}%</span>
                </div>
              </div>

              <div className="dashboard-status-legend">
                <div className="dashboard-status-legend-item">
                  <span className="dashboard-status-dot dashboard-status-dot-active" />
                  <span>Active</span>
                  <strong>{activeMonitoringCount}</strong>
                </div>
                <div className="dashboard-status-legend-item">
                  <span className="dashboard-status-dot dashboard-status-dot-inactive" />
                  <span>Inactive</span>
                  <strong>{inactiveMonitoringCount}</strong>
                </div>
              </div>
            </div>
            <div className='button-wrapper'>

            <button
              type="button"
              className="dashboard-action-button"
              onClick={() => navigate('/ruleaza-monitoring')}
            >
              Ruleaza monitorizare
            </button>
            </div>
          </div>

          <div className="chart-card chart-small dashboard-add-card">
            <h4>Adauga monitorizare</h4>
            <div className="dashboard-add-card-body">
              <button
                type="button"
                className="dashboard-add-button"
                onClick={() => navigate('/monitorizare')}
                aria-label="Adauga monitorizare"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div className="chart-card chart-wide">
          <div className="chart-card-header">
            <h4>Rezultate monitorizare în timp</h4>
            <div className="dashboard-interval-controls">
              <select
                className="dashboard-interval-select"
                value={monitoringInterval}
                onChange={(event) => setMonitoringInterval(event.target.value)}
              >
                {monitoringIntervalOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {monitoringInterval === 'custom' && (
                <>
                  <select
                    className="dashboard-interval-select"
                    value={customStartLabel}
                    onChange={(event) => setCustomStartLabel(event.target.value)}
                  >
                    {monitoringTimeline.map((item) => (
                      <option key={`start-${item.label}`} value={item.label}>
                        {item.label}
                      </option>
                    ))}
                  </select>

                  <span className="dashboard-interval-separator">→</span>

                  <select
                    className="dashboard-interval-select"
                    value={customEndLabel}
                    onChange={(event) => setCustomEndLabel(event.target.value)}
                  >
                    {monitoringTimeline.map((item) => (
                      <option key={`end-${item.label}`} value={item.label}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>

          <Bar
            data={monitoringResultsData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
              },
              scales: {
                y: { beginAtZero: true },
              },
            }}
          />
        </div>

      </div>

      {isDetailOpen && (
        <div className="dashboard-modal-overlay" onClick={closeDetailModal}>
          <div className="dashboard-modal" onClick={(event) => event.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h3>Detalii rezultate monitorizare</h3>
              <div className="dashboard-modal-header-actions">
                <button
                  type="button"
                  className="dashboard-action-button dashboard-modal-delete-button"
                  onClick={deleteMonitoringResult}
                  disabled={detailLoading || deleting}
                >
                  {deleting ? 'Ștergere...' : 'Șterge rezultatul'}
                </button>
                <button type="button" className="dashboard-modal-close" onClick={closeDetailModal}>×</button>
              </div>
            </div>

            <div className="dashboard-modal-body">
              {detailLoading && <div className="dashboard-loading">Se încarcă detaliile...</div>}
              {detailError && <div className="dashboard-error">{detailError}</div>}

              {!detailLoading && !detailError && detailResult && (
                <div className="dashboard-monitoring-details">
                  <div className="dashboard-monitoring-summary">
                    <div className="dashboard-monitoring-summary-card">
                      <strong>Marcă</strong>
                      <span>{detailResult.marca || '-'}</span>
                    </div>
                    <div className="dashboard-monitoring-summary-card">
                      <strong>Period</strong>
                      <span>{detailResult.search_period?.from} → {detailResult.search_period?.to}</span>
                    </div>
                    <div className="dashboard-monitoring-summary-card">
                      <strong>Total Matches</strong>
                      <span>{detailResult.total_matches ?? 0}</span>
                    </div>
                    <div className="dashboard-monitoring-summary-card">
                      <strong>Generated</strong>
                      <span>{formatDate(detailResult.timestamp)}</span>
                    </div>
                  </div>

                  {Object.entries(detailResult.keywords_results || {}).map(([keyword, keywordData]) => (
                    <div key={keyword} className="dashboard-keyword-block">
                      <button
                        type="button"
                        className="dashboard-keyword-toggle"
                        onClick={() => setExpandedKeywords((prev) => ({ ...prev, [keyword]: !prev[keyword] }))}
                      >
                        <span className="dashboard-keyword-title">
                          Keyword: <strong>{keyword}</strong> ({getKeywordResultsLabel(keywordData)})
                        </span>
                        <span className={`dashboard-toggle-arrow ${expandedKeywords[keyword] ? 'open' : ''}`}>▾</span>
                      </button>

                      {expandedKeywords[keyword] && (
                        <div className="dashboard-keyword-content">
                          {getKeywordError(keywordData) && (
                            <div className="dashboard-error">{getKeywordError(keywordData)}</div>
                          )}

                          {['critical', 'warning', 'moderate'].map((level) => {
                            const trademarks = keywordData.grouped?.[level] || [];
                            const levelKey = `${keyword}__${level}`;
                            const isLevelOpen = !!expandedLevels[levelKey];

                            return (
                              <div key={level} className="dashboard-level-block">
                                <button
                                  type="button"
                                  className="dashboard-level-toggle"
                                  onClick={() => setExpandedLevels((prev) => ({ ...prev, [levelKey]: !prev[levelKey] }))}
                                >
                                  <span className={`dashboard-level-badge ${level}`}>
                                    {levelLabels[level]} ({trademarks.length})
                                  </span>
                                  <span className={`dashboard-toggle-arrow ${isLevelOpen ? 'open' : ''}`}>▾</span>
                                </button>

                                {isLevelOpen && trademarks.length > 0 && (
                                  <div className="dashboard-detail-table-wrap">
                                    <table className="dashboard-detail-table">
                                      <thead>
                                        <tr>
                                          <th>Name</th>
                                          <th>Office</th>
                                          <th>Applicant(s)</th>
                                          <th>Nice Classes</th>
                                          <th>Application Date</th>
                                          <th>Similarity %</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {trademarks.map((trademark, index) => {
                                          const data = trademark.data || {};
                                          const applicants = Array.isArray(data.applicantName) ? data.applicantName.join(', ') : '-';
                                          const classes = Array.isArray(data.niceClass) ? data.niceClass.join(', ') : '-';

                                          return (
                                            <tr key={`${level}-${index}`}>
                                              <td>{data.tmName || '-'}</td>
                                              <td>{data.tmOffice || '-'}</td>
                                              <td>{applicants || '-'}</td>
                                              <td>{classes || '-'}</td>
                                              <td>{formatDate(data.applicationDate)}</td>
                                              <td>{typeof trademark.similarity_percent === 'number' ? trademark.similarity_percent.toFixed(2) : '-'}%</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}

                                {isLevelOpen && trademarks.length === 0 && (
                                  <div className="dashboard-results-empty">Nu există rezultate în această categorie.</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;